import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  recipeRequests,
  type CreateRecipeInput,
  type UpdateRecipeInput,
  type CheckAvailabilityInput,
  type IngredientInput,
  type UpdateIngredientInput,
  type CreateShelfLifeInput,
  type UpdateShelfLifeInput,
  type CreateStorageContainerInput,
  type UpdateStorageContainerInput,
  type LinkStorageContainerInput,
  type UpdateRecipeStorageContainerInput,
  type CreateToolInput,
  type LinkToolInput,
  type UpdateRecipeToolInput,
  type CreateRecipeVersionInput,
  type RecipeStageInput,
} from '@/lib/api/requests/recipes';
import { recipeKeys } from './keys';

export function useCreateRecipe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRecipeInput) => recipeRequests.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: recipeKeys.categories() });
    },
  });
}

export function useUpdateRecipe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateRecipeInput }) =>
      recipeRequests.update(id, data),
    onSuccess: (result, { id }) => {
      // Write the API response directly into the cache so the form re-initializes
      // correctly if the user navigates away and back, without an extra round-trip.
      queryClient.setQueryData(recipeKeys.detail(id), result);
      // Food cost and margin follow the saved selling price.
      queryClient.invalidateQueries({ queryKey: recipeKeys.costSummary(id) });
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: recipeKeys.categories() });
    },
  });
}

export function useDeleteRecipe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => recipeRequests.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: recipeKeys.categories() });
    },
  });
}

export function useCheckRecipeAvailability() {
  return useMutation({
    mutationFn: ({ recipeId, data }: { recipeId: number; data: CheckAvailabilityInput }) =>
      recipeRequests.checkAvailability(recipeId, data),
  });
}

export function useBulkDeleteRecipes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => recipeRequests.bulkDelete(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: recipeKeys.categories() });
    },
  });
}

export function useReplaceIngredients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ingredients }: { id: number; ingredients: IngredientInput[] }) =>
      recipeRequests.replaceIngredients(id, ingredients),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: recipeKeys.ingredients(id) });
      void queryClient.invalidateQueries({ queryKey: recipeKeys.costSummary(id) });
    },
  });
}

export function useAddIngredient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: IngredientInput }) =>
      recipeRequests.addIngredient(id, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: recipeKeys.ingredients(id) });
      void queryClient.invalidateQueries({ queryKey: recipeKeys.costSummary(id) });
    },
  });
}

export function useRemoveIngredient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ingredientId }: { id: number; ingredientId: number }) =>
      recipeRequests.removeIngredient(id, ingredientId),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: recipeKeys.ingredients(id) });
      void queryClient.invalidateQueries({ queryKey: recipeKeys.costSummary(id) });
    },
  });
}

export function useUpdateIngredient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recipeId, ingredientId, data }: { recipeId: number; ingredientId: number; data: UpdateIngredientInput }) =>
      recipeRequests.updateIngredient(recipeId, ingredientId, data),
    onSuccess: (_result, { recipeId }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.ingredients(recipeId) });
      queryClient.invalidateQueries({ queryKey: recipeKeys.costSummary(recipeId) });
    },
  });
}

export function useBulkUpdateRecipeStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isPublished }: { ids: number[]; isPublished: boolean }) =>
      recipeRequests.bulkUpdateStatus(ids, isPublished),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
    },
  });
}

export function useCreateShelfLife() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recipeId, data }: { recipeId: string; data: CreateShelfLifeInput }) =>
      recipeRequests.createShelfLife(recipeId, data),
    onSuccess: (_result, { recipeId }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.shelfLife(recipeId) });
    },
  });
}

export function useUpdateShelfLife() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recipeId, id, data }: { recipeId: string; id: string; data: UpdateShelfLifeInput }) =>
      recipeRequests.updateShelfLife(recipeId, id, data),
    onSuccess: (_result, { recipeId }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.shelfLife(recipeId) });
    },
  });
}

export function useDeleteShelfLife() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recipeId, id }: { recipeId: string; id: string }) =>
      recipeRequests.deleteShelfLife(recipeId, id),
    onSuccess: (_result, { recipeId }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.shelfLife(recipeId) });
    },
  });
}

export function useCreateStorageContainerCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateStorageContainerInput) => recipeRequests.createStorageContainerCatalog(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage-containers', 'catalog-picker'] });
    },
  });
}

export function useUpdateStorageContainerCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateStorageContainerInput }) =>
      recipeRequests.updateStorageContainerCatalog(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage-containers', 'catalog-picker'] });
    },
  });
}

export function useCreateToolCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateToolInput) => recipeRequests.createToolCatalog(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tools', 'catalog-picker'] });
    },
  });
}

export function useLinkStorageContainer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recipeId, data }: { recipeId: number; data: LinkStorageContainerInput }) =>
      recipeRequests.linkStorageContainer(recipeId, data),
    onSuccess: (_result, { recipeId }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.storageContainers(recipeId) });
    },
  });
}

export function useUpdateRecipeStorageContainer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recipeId, id, data }: { recipeId: number; id: number; data: UpdateRecipeStorageContainerInput }) =>
      recipeRequests.updateRecipeStorageContainer(recipeId, id, data),
    onSuccess: (_result, { recipeId }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.storageContainers(recipeId) });
    },
  });
}

export function useUnlinkStorageContainer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recipeId, id }: { recipeId: number; id: number }) =>
      recipeRequests.unlinkStorageContainer(recipeId, id),
    onSuccess: (_result, { recipeId }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.storageContainers(recipeId) });
    },
  });
}

export function useLinkTool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recipeId, data }: { recipeId: number; data: LinkToolInput }) =>
      recipeRequests.linkTool(recipeId, data),
    onSuccess: (_result, { recipeId }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.tools(recipeId) });
    },
  });
}

export function useUpdateRecipeTool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recipeId, id, data }: { recipeId: number; id: number; data: UpdateRecipeToolInput }) =>
      recipeRequests.updateRecipeTool(recipeId, id, data),
    onSuccess: (_result, { recipeId }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.tools(recipeId) });
    },
  });
}

export function useUnlinkTool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recipeId, id }: { recipeId: number; id: number }) =>
      recipeRequests.unlinkTool(recipeId, id),
    onSuccess: (_result, { recipeId }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.tools(recipeId) });
    },
  });
}

export function useDuplicateRecipe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => recipeRequests.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
    },
  });
}

export function useCreateRecipeVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateRecipeVersionInput }) =>
      recipeRequests.createVersion(id, data),
    onSuccess: (_result, { id }) => {
      queryClient.invalidateQueries({ queryKey: recipeKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: recipeKeys.lists() });
    },
  });
}

/** Saves the method's stages as a whole list. */
export function useReplaceRecipeStages(recipeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stages: RecipeStageInput[]) => recipeRequests.replaceStages(recipeId, stages),
    onSuccess: (stages) => {
      queryClient.setQueryData(recipeKeys.stages(recipeId), stages);
    },
  });
}
